// Day, twilight and night for the booking calendar and the booking form.
// Pure and offline: built on sunTimes, with no web service behind it.
//
//   day       the sun's upper limb is above the horizon (sunrise to sunset)
//   twilight  sunset to the end of evening civil twilight, and the morning
//             equivalent: still legal VFR daylight flying
//   night     EASA night, the end of evening civil twilight to the beginning
//             of morning civil twilight: a night rating and a night-rated
//             aircraft are needed

import { sunTimes, type SunEvents } from './sunTimes'

export type Light = 'day' | 'twilight' | 'night'

export interface LatLon {
  lat: number
  lon: number
}

// The club's home field when the flightLog.homeBase setting, or its
// coordinates, cannot be read: Hyvinkää (EFHV), the same default as
// DEFAULT_HOME_BASE. The app takes the real one from GET /api/time.
export const CLUB_LOCATION: LatLon = { lat: 60.6544, lon: 24.8811 }
export const CLUB_AERODROME = 'EFHV'

// The home field as GET /api/time sends it.
export interface HomeBasePoint extends LatLon {
  code: string
}

export interface LightBand {
  from: number
  to: number
  light: Light
}

const DAY_MS = 86_400_000

// The solar calendar day of an instant, as sunTimes keys it.
function solarDayKey(ms: number, lon: number): string {
  return new Date(ms + lon * 240_000).toISOString().slice(0, 10)
}

function shiftKey(key: string, days: number): string {
  return new Date(Date.parse(`${key}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10)
}

function insideEvents(events: SunEvents, ms: number): boolean {
  if (events.state === 'alwaysUp') return true
  if (events.state === 'alwaysDown') return false
  return ms >= Date.parse(events.rise!) && ms < Date.parse(events.set!)
}

export function lightAt(ms: number, { lat, lon }: LatLon = CLUB_LOCATION): Light {
  const { sun, civilTwilight } = sunTimes(solarDayKey(ms, lon), lat, lon)
  if (insideEvents(sun, ms)) return 'day'
  return insideEvents(civilTwilight, ms) ? 'twilight' : 'night'
}

// The consecutive light bands covering [from, to), for shading a time axis.
export function lightBands(from: number, to: number, place: LatLon = CLUB_LOCATION): LightBand[] {
  if (to <= from) return []
  const firstKey = shiftKey(solarDayKey(from, place.lon), -1)
  const days = Math.ceil((to - from) / DAY_MS) + 3
  const cuts = new Set<number>()
  for (let i = 0; i < days; i++) {
    const { sun, civilTwilight } = sunTimes(shiftKey(firstKey, i), place.lat, place.lon)
    for (const events of [sun, civilTwilight]) {
      if (events.state !== 'normal') continue
      for (const iso of [events.rise!, events.set!]) {
        const ms = Date.parse(iso)
        if (ms > from && ms < to) cuts.add(ms)
      }
    }
  }
  const points = [from, ...[...cuts].sort((a, b) => a - b), to]
  const bands: LightBand[] = []
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i]!
    const end = points[i + 1]!
    const light = lightAt((start + end) / 2, place)
    const last = bands[bands.length - 1]
    if (last && last.light === light) last.to = end
    else bands.push({ from: start, to: end, light })
  }
  return bands
}

export interface NightCheck {
  startsInNight: boolean
  endsInNight: boolean
}

// A booking "involves night" when it starts or ends in it.
export function nightCheck(startsAt: Date | number, endsAt: Date | number, place: LatLon = CLUB_LOCATION): NightCheck {
  const start = new Date(startsAt).getTime()
  const end = new Date(endsAt).getTime()
  return {
    startsInNight: lightAt(start, place) === 'night',
    // The end instant itself is the moment the aircraft is back; look just inside.
    endsInNight: lightAt(end - 1, place) === 'night',
  }
}
